"use client";

import { useState } from "react";
import Link from "next/link";
import { useAction } from "next-safe-action/hooks";
import { Check, Loader2, Mail } from "lucide-react";
import { toast } from "sonner";

import { AuthShell } from "@/components/auth/auth-shell";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { requestPasswordReset } from "@/lib/actions/auth";

export default function ResetPasswordPage() {
  const [email, setEmail] = useState("");
  const [submitted, setSubmitted] = useState(false);

  const { execute, status } = useAction(requestPasswordReset, {
    onSuccess: () => {
      setSubmitted(true);
    },
    onError: () => {
      // Always show success to prevent email enumeration
      setSubmitted(true);
    },
  });

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();

    if (!email) {
      toast.error("Please enter your email address");
      return;
    }

    execute({ email });
  };

  const isLoading = status === "executing";

  if (submitted) {
    return (
      <AuthShell
        title="Check your email"
        subtitle="If an account exists for that email, we've sent password reset instructions"
      >
        <div className="space-y-6">
          {/* Success icon */}
          <div className="flex justify-center">
            <div className="flex h-16 w-16 items-center justify-center rounded-full bg-accent/10">
              <Check className="h-8 w-8 text-accent" strokeWidth={2.5} />
            </div>
          </div>

          {/* Instructions */}
          <div className="space-y-3 text-center">
            <div className="flex items-center justify-center gap-2 text-sm text-muted-foreground">
              <Mail className="h-4 w-4" />
              <span>Email sent to</span>
            </div>
            <p className="text-sm font-medium text-foreground break-all">{email}</p>
          </div>

          {/* Additional info */}
          <div className="rounded-md bg-muted/50 p-4 text-sm text-muted-foreground">
            <p className="mb-2 font-medium text-foreground">What's next?</p>
            <ul className="space-y-2 text-xs">
              <li className="flex gap-2">
                <span className="text-accent">•</span>
                <span>Check your inbox and spam folder</span>
              </li>
              <li className="flex gap-2">
                <span className="text-accent">•</span>
                <span>Click the reset link within 1 hour</span>
              </li>
              <li className="flex gap-2">
                <span className="text-accent">•</span>
                <span>If you don't receive an email, the address may not be registered</span>
              </li>
            </ul>
          </div>

          {/* Actions */}
          <div className="space-y-3">
            <Button
              variant="outline"
              className="w-full"
              onClick={() => {
                setSubmitted(false);
                setEmail("");
              }}
            >
              Try a different email
            </Button>

            <div className="text-center text-sm">
              <span className="text-muted-foreground">Remember your password? </span>
              <Link
                href="/login"
                className="font-medium text-accent hover:text-accent/90 transition-colors"
              >
                Sign in
              </Link>
            </div>
          </div>
        </div>
      </AuthShell>
    );
  }

  return (
    <AuthShell
      title="Reset password"
      subtitle="Enter your email address and we'll send you a link to reset your password"
    >
      <form onSubmit={handleSubmit} className="space-y-5">
        <div className="space-y-2">
          <Label htmlFor="email">Email</Label>
          <Input
            id="email"
            type="email"
            placeholder="name@example.com"
            value={email}
            onChange={(e) => setEmail(e.target.value)}
            disabled={isLoading}
            autoComplete="email"
            autoFocus
            required
          />
        </div>

        <Button type="submit" className="w-full" disabled={isLoading}>
          {isLoading && <Loader2 className="mr-2 h-4 w-4 animate-spin" />}
          Send reset link
        </Button>
      </form>

      <div className="mt-6 text-center text-sm">
        <span className="text-muted-foreground">Remember your password? </span>
        <Link
          href="/login"
          className="font-medium text-accent hover:text-accent/90 transition-colors"
        >
          Sign in
        </Link>
      </div>
    </AuthShell>
  );
}
